import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, X, MoonStar } from "lucide-react";
import { Logo } from "@/components/Logo";
import { scrollToId } from "@/lib/scroll";

const LINKS = [
  { id: "minusta", label: "Minusta" },
  { id: "palautteet", label: "Palaute" },
  { id: "palvelut", label: "Palvelut" },
  { id: "ilta-ja-yo", label: "Ilta & yö" },
];

export const goContact = () => scrollToId("yhteydenotto");

export const Nav = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (id) => {
    setOpen(false);
    setTimeout(() => scrollToId(id), open ? 250 : 0);
  };

  return (
    <>
      <header
        className={`fixed inset-x-0 top-0 z-50 transition-all duration-500 ${
          scrolled ? "bg-cream/85 backdrop-blur-xl border-b border-line/70 py-3" : "bg-transparent py-5"
        }`}
      >
        <div className="mx-auto flex max-w-7xl items-center justify-between px-5 sm:px-8">
          <Logo onClick={() => window.__lenis ? window.__lenis.scrollTo(0, { duration: 1.2 }) : window.scrollTo({ top: 0, behavior: "smooth" })} />
          <nav className="hidden items-center gap-8 lg:flex">
            {LINKS.map((l) => (
              <button
                key={l.id}
                data-testid={`navbar-nav-link-${l.id}`}
                onClick={() => go(l.id)}
                className="group relative text-sm font-medium text-ink/80 transition-colors hover:text-petrol"
              >
                {l.label}
                <span className="absolute -bottom-1 left-0 h-px w-0 bg-beige-deep transition-all duration-300 group-hover:w-full" />
              </button>
            ))}
            <button
              data-testid="navbar-cta-contact-button"
              onClick={goContact}
              className="rounded-full bg-petrol px-6 py-3 text-sm font-semibold text-cream shadow-[0_10px_30px_-12px_rgba(18,58,62,0.6)] transition-all duration-300 hover:-translate-y-0.5 hover:bg-petrol-dark"
            >
              Ota yhteyttä
            </button>
          </nav>
          <button
            className="flex h-11 w-11 items-center justify-center rounded-full border border-line bg-cream/70 text-petrol lg:hidden"
            onClick={() => setOpen(true)}
            data-testid="navbar-mobile-open-button"
            aria-label="Avaa valikko"
          >
            <Menu className="h-5 w-5" strokeWidth={1.5} />
          </button>
        </div>
      </header>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="fixed inset-0 z-[70] flex flex-col bg-petrol-night text-cream"
          >
            <div className="flex items-center justify-between px-5 py-5 sm:px-8">
              <Logo light onClick={() => { setOpen(false); window.scrollTo({ top: 0 }); }} />
              <button
                className="flex h-11 w-11 items-center justify-center rounded-full border border-cream/20"
                onClick={() => setOpen(false)}
                data-testid="navbar-mobile-close-button"
                aria-label="Sulje valikko"
              >
                <X className="h-5 w-5" strokeWidth={1.5} />
              </button>
            </div>
            <nav className="flex flex-1 flex-col justify-center gap-2 px-8">
              {LINKS.map((l, i) => (
                <motion.button
                  key={l.id}
                  initial={{ opacity: 0, x: -24 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.08 * i + 0.1, duration: 0.5 }}
                  onClick={() => go(l.id)}
                  className="border-b border-cream/10 py-4 text-left font-serif text-4xl font-medium tracking-tight text-cream transition-colors hover:text-beige"
                >
                  {l.label}
                </motion.button>
              ))}
            </nav>
            <div className="px-8 pb-10">
              <button
                data-testid="navbar-mobile-cta-button"
                onClick={goContact}
                className="w-full rounded-full bg-cream py-4 text-base font-semibold text-petrol"
              >
                Ota yhteyttä
              </button>
              <p className="mt-5 flex items-center justify-center gap-2 text-xs text-cream/50">
                <MoonStar className="h-4 w-4" strokeWidth={1.5} /> Helsinki · Espoo · Vantaa
              </p>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};
